import dayjs from 'dayjs'
import { get, isArray, isNil, isObject, lowerCase, startCase } from 'lodash'
import {
  CornerUpRight,
  FileCheck,
  FilePenLine,
  History,
  LucideIcon,
  Plus,
  SearchX,
  Trash2,
  UserSearch,
} from 'lucide-react'

import { PROJECT_TYPE_OPTIONS } from '@/features/project/constants'
import i18n from '@/i18n'
import { EntityAuditLogEntry } from '@/schema'

const MINUTES_IN_HOUR = 60
const HOURS_IN_DAY = 24

export enum AuditLogAction {
  CREATED = 'CREATED',
  UPDATED = 'UPDATED',
  DELETED = 'DELETED',
  ACTIONABLE = 'ACTIONABLE',
  REQUESTED = 'REQUESTED',
  CANCELLED = 'CANCELLED',
  DELEGATED = 'DELEGATED',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  REVISIONED = 'REVISIONED',
}

export const getRelativeTime = (isoDate: string): string => {
  const now = dayjs()
  const date = dayjs(isoDate)
  const diffMinutes = now.diff(date, 'minute')
  const diffHours = now.diff(date, 'hour')

  if (diffMinutes < 1) return 'Just now'
  if (diffMinutes < MINUTES_IN_HOUR)
    return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`
  if (diffHours < HOURS_IN_DAY)
    return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
  return date.format('MMM D, YYYY')
}

export const getAbsoluteTime = (isoDate: string): string =>
  dayjs(isoDate).format('hh:mm A')

export const getLogTypeIcon = (logType: string): LucideIcon => {
  const upper = logType.toUpperCase()
  if (
    upper.includes(AuditLogAction.CREATED) ||
    upper.includes(AuditLogAction.ACTIONABLE)
  )
    return Plus
  if (upper.includes(AuditLogAction.DELETED)) return Trash2
  if (upper.includes(AuditLogAction.REQUESTED)) return UserSearch
  if (upper.includes(AuditLogAction.CANCELLED)) return SearchX
  if (upper.includes(AuditLogAction.DELEGATED)) return CornerUpRight
  if (upper.includes(AuditLogAction.APPROVED)) return FileCheck
  if (
    upper.includes(AuditLogAction.REJECTED) ||
    upper.includes(AuditLogAction.REVISIONED)
  )
    return History
  return FilePenLine
}

export const truncateElementsToMax = (
  elements: Array<string | React.ReactElement>,
  limit: number = 50
): { elements: Array<string | React.ReactElement>; truncated: boolean } => {
  const result: Array<string | React.ReactElement> = []
  let currentLen = 0
  let truncated = false

  for (const el of elements) {
    if (typeof el === 'string') {
      if (currentLen + el.length > limit) {
        result.push(el.slice(0, limit - currentLen) + '…')
        truncated = true
        break
      } else {
        result.push(el)
        currentLen += el.length
      }
    } else {
      const name = el.props?.userName || ''
      if (currentLen + name.length > limit) {
        result.push(el, '…')
        truncated = true
        break
      } else {
        result.push(el)
        currentLen += name.length
      }
    }
  }

  return { elements: result, truncated }
}

// ---------------------------------------------------------------------------
// Audit Configuration
// ---------------------------------------------------------------------------

/**
 * Configuration for mapping raw backend entity types and field names to user-friendly UI labels.
 *
 * @example
 * // How to add a new Entity mapping:
 * // If backend sends log_type="INVOICE_UPDATED" or type_entity="invoice",
 * // the normalized key will be 'invoice'. Add it to the `entities` object:
 * entities: {
 *   // ...
 *   invoice: 'invoice',
 * }
 *
 * // How to add a new Field mapping:
 * // If backend sends field="invoice.amount" or "invoice_amount",
 * // the `formatField` function normalizes it to "invoice_amount" (dots become underscores).
 * // Add it to the `fields` object:
 * fields: {
 *   // ...
 *   invoice_amount: 'total amount',
 * }
 */
export const AUDIT_CONFIG = {
  entities: {
    company: 'company',
    project: 'project',
    deliverable_set: 'deliverable set',
    deliverableset: 'deliverable set',
  },
  fields: {
    project_type: 'type',
    reporting_event: 'reporting event list',
    reporting_events: 'reporting event list',
    reporting_event_ids: 'reporting event list',
    dataset_version: 'dataset version list',
    dataset_versions: 'dataset version list',
    dataset_version_ids: 'dataset version list',
    reviewer: 'reviewer list',
    reviewers: 'reviewer list',
    owner: 'owner',
    owner_id: 'owner',
    target_date: 'target date',
    due_date: 'target date',
    associated_company: 'associated company',
    company_id: 'associated company',
    company: 'associated company',
    project_company: 'associated company',
    associated_project: 'associated project',
    project_id: 'associated project',
    project: 'associated project',
    deliverable_set_name: 'name',
    'deliverable set name': 'name',
    name: 'name',
    status: 'status',
  },
}

export const formatField = (field: string) => {
  const normalizedKey = String(field).toLowerCase().trim().replace(/\./g, '_') //NOSONAR
  return get(AUDIT_CONFIG.fields, normalizedKey, lowerCase(field))
}

export const formatEntityType = (typeEntity: string) => {
  const normalizedKey = String(typeEntity).toLowerCase().trim()
  return get(AUDIT_CONFIG.entities, normalizedKey, lowerCase(typeEntity))
}

export const parseLogType = (logType: string) => {
  const upper = String(logType).toUpperCase()
  const match =
    /_(CREATED|UPDATED|DELETED|REQUESTED|CANCELLED|DELEGATED|APPROVED|REJECTED|REVISIONED)$/.exec(
      upper
    )
  const action = get(match, '[1]', AuditLogAction.UPDATED) as AuditLogAction
  const rawEntityPattern = new RegExp(`_${action}$`)
  const rawEntity = upper.replace(rawEntityPattern, '').toLowerCase()
  return { action, rawEntity }
}

export const formatLogType = (logType: string) =>
  startCase(logType.toLowerCase())

const normalizePrimitive = (val: string | number | boolean): string => {
  const str = String(val)
  if (str.startsWith('ProjectType.')) {
    return str.replace('ProjectType.', '').replace(/_/g, ' ') //NOSONAR
  }
  return str
}

const normalizeObject = (obj: Record<string, unknown>): string => {
  if (obj.first_name || obj.last_name) {
    return [obj.first_name, obj.last_name].filter(Boolean).join(' ').trim()
  }
  if (obj.user_name && typeof obj.user_name === 'string') {
    return obj.user_name
  }
  if ('email' in obj && obj.email) {
    return typeof obj.email === 'string' ? obj.email : JSON.stringify(obj.email)
  }
  if ('data' in obj) return normalizeLogValue(obj.data)
  if ('name' in obj) return normalizeLogValue(obj.name)
  return JSON.stringify(obj)
}

export const normalizeLogValue = (val: unknown): string => {
  if (isNil(val)) return ''
  if (
    typeof val === 'string' ||
    typeof val === 'number' ||
    typeof val === 'boolean'
  ) {
    return normalizePrimitive(val)
  }
  if (isArray(val)) {
    return val.map((v) => normalizeLogValue(v)).join(', ')
  }
  if (isObject(val)) {
    return normalizeObject(val as Record<string, unknown>)
  }
  return JSON.stringify(val) ?? ''
}

export enum AuditLogField {
  STATUS = 'status',
  NAME = 'name',
}

export const formatFieldValue = (field: string, value: unknown): string => {
  const normalizedValue = normalizeLogValue(value)
  const normalizedKey = String(field).toLowerCase().trim().replace(/\./g, '_') //NOSONAR

  if (normalizedKey.endsWith(AuditLogField.STATUS)) {
    return startCase(normalizedValue.toLowerCase())
  }

  // Handle date fields
  if (normalizedKey.endsWith('date')) {
    const d = dayjs(String(value))
    if (d.isValid()) {
      return d.format('MM/DD/YYYY')
    }
  }

  // Add other specific field formatting here if needed
  if (normalizedKey === 'project_type') {
    const valLower = normalizedValue.toLowerCase()

    const option = PROJECT_TYPE_OPTIONS.find((opt) => {
      const optValLower = opt.value.toLowerCase()
      return (
        valLower === optValLower || valLower === optValLower.replace(/_/g, ' ') //NOSONAR
      )
    })

    if (option) {
      return i18n.t(option.label)
    }
  }

  if (normalizedKey.endsWith(AuditLogField.NAME)) {
    return normalizedValue
  }

  return normalizedValue
}

export const isToday = (isoDate: string): boolean =>
  dayjs(isoDate).isSame(dayjs(), 'day')

export const toGroupKey = (isoDate: string): string => {
  if (isToday(isoDate)) return '__TODAY__'
  return dayjs(isoDate).format('MM/DD/YYYY')
}

export interface DateGroup {
  key: string
  label: string
  isToday: boolean
  entries: EntityAuditLogEntry[]
}

export const groupByDate = (
  entries: EntityAuditLogEntry[],
  todayLabel: string
): DateGroup[] => {
  const map = new Map<string, EntityAuditLogEntry[]>()

  for (const entry of entries) {
    const key = toGroupKey(entry.created_at)
    if (!map.has(key)) map.set(key, [])
    map.get(key)!.push(entry)
  }

  return Array.from(map.entries()).map(([key, items]) => ({
    key,
    label: key === '__TODAY__' ? todayLabel : key,
    isToday: key === '__TODAY__',
    entries: items,
  }))
}
